import _ from 'lodash';
import moment from 'moment';

import {
  RegistrationSituationStatus,
  RenewalOrderStatus,
  RenewalStatus,
} from '@appTypes/enums';
import type {
  OrgAdultsRoster,
  OrgYouthsRoster,
  RegistrarInfo,
} from '@appTypes/esb';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { PositionId } from '@modules/shared/constants';

import type { RosterScout } from '../duck/types';

export const getIsDenChief = (scout: RosterScout) =>
  scout.positions?.some(({ positionId }) => positionId === PositionId.DenChief);

export const dedupeMembers = (scouts: RosterScout[]) =>
  Object.values(
    scouts.reduce((acc, scout) => {
      const { personGuid, userId } = scout;
      const key = `${personGuid}-${userId}`;
      return {
        ...acc,
        [key]: { ...acc[key], ...scout },
      };
    }, {} as Record<string, RosterScout>),
  );

export function findRegistrationRecord(
  items: Array<RegistrarInfo & { personGuid: string }>,
  personGuid: string,
) {
  const person = _.chain(items)
    .filter(item => item.personGuid === personGuid)
    .orderBy(
      ['registrationExpireDt', 'registrationEffectiveDt'],
      ['desc', 'desc'],
    )
    .first()
    .value();

  return person;
}

const getRegistrationStatus = (registrarInfo: RegistrarInfo) => {
  const { registrationExpireDt, isManuallyEnded } = registrarInfo;

  if (isManuallyEnded || !registrationExpireDt) {
    return RegistrationSituationStatus.Expired;
  }

  let regStatus = RegistrationSituationStatus.Active;
  const today = moment();
  // Lapsed is 3 months - MFSR-2545
  const lapsedRange = moment(registrationExpireDt).add(3, 'months');
  const regExpireDateStartOfNextDay = moment(registrationExpireDt)
    .clone()
    .add(1, 'day')
    .startOf('day');

  if (today.isAfter(moment(regExpireDateStartOfNextDay))) {
    if (today.isBetween(regExpireDateStartOfNextDay, lapsedRange)) {
      regStatus = RegistrationSituationStatus.Lapsed;
    } else {
      regStatus = RegistrationSituationStatus.Expired;
    }
  }

  return regStatus;
};

const getRenewalStatus = (registrarInfo: RegistrarInfo) => {
  const {
    isAutoRenewalOptedOut,
    isYearlyMembershipRenewalPaid,
    registrationExpireDt,
    renewalStatus,
  } = registrarInfo;
  // ESB has the proper logic in place
  if (featureFlags.getFlag('SBL_5829_REAL_RENEWAL_STATUS'))
    return renewalStatus;
  if (isAutoRenewalOptedOut) return RenewalStatus.OptedOut;
  if (isYearlyMembershipRenewalPaid) return RenewalStatus.Renewed;
  if (moment().isAfter(moment(registrationExpireDt)))
    return RenewalStatus.Expired;
  if (moment(registrationExpireDt).isBefore(moment().add(2, 'months')))
    return RenewalStatus.Eligible;

  return RenewalStatus.Current;
};

export const getMembersRenewalObject = (
  orgInfo: OrgAdultsRoster | OrgYouthsRoster,
) => {
  if (!orgInfo?.members) return [];

  return orgInfo.members.map(
    ({ registrarInfo, personFullName, personGuid }) => {
      const renewalStatus = getRenewalStatus(registrarInfo);
      const registrationSituationStatus = getRegistrationStatus(registrarInfo);

      return {
        personFullName,
        personGuid,
        renewalStatus,
        registrationSituationStatus,
        bsaOrderId: registrarInfo.bsaOrderId,
        isYearlyMembershipRenewalPaid:
          registrarInfo.isYearlyMembershipRenewalPaid,
        registrationExpireDt: moment(registrarInfo.registrationExpireDt),
        registrationEffectiveDt: moment(registrarInfo.registrationEffectiveDt),
        registrationId: registrarInfo.registrationId,
      };
    },
  );
};

export function checkHasOrderStatusFilter(filter: Record<string, boolean>) {
  // By default other filters work like that, if all not checked is as if the filter doesn't matter
  const checks = Object.keys(RenewalOrderStatus).map(
    key => filter[`order_status_${key}`],
  );
  const isAllActive = checks.every(active => active);
  const isAllInactive = checks.every(active => !active);

  return !(isAllActive || isAllInactive);
}
